import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import {
	CreditRequestResponse,
	FepaCreditResourceService,
	PrepaCaseResourceService,
	PrepaDualFileResourceService,
	PrepaMediationResourceService,
	PrepaPowerBiReportResourceService,
	StaffAssignmentVO,
	SystemicCaseChargeResponse,
	SystemicCaseResponse,
	SystemicCaseVO,
	SystemicChargeVO,
	TransfersResponseVO,
} from 'src/app/services/gen';
import { SystemicResourceService } from 'src/app/services/gen/api/systemicResource.service';
import { TransferResourceService } from 'src/app/services/gen/api/transferResource.service';
import { CaseTransferOutcomeVO } from 'src/app/services/gen/model/caseTransferOutcomeVO';
import { DualFileResponse } from 'src/app/services/Utils/dualFileResponse';
import { TransferRequestVO } from 'src/app/services/gen/model/transferRequestVO';
import {
	AppealAssignmentVO,
	AppealDashboard,
	LitigationAppealDashboardResourceService,
	LitigationAppealResourceService,
} from 'src/app/services/LitigationWebService';
import { SearchDataService, WsCasesResponseInterface } from 'src/app/services/search-data.service';
import { CaseRequestParam, PendingReportResponseSummary, PrepaSearchResourceService, ReportResourceService } from 'src/app/services/SearchDataWebService';
import { UserResourceService } from 'src/app/services/UserManagement';
import { LookUpStatusCode } from 'src/app/shared/common-structures/app.constants';
import { CaseDetailV2 } from 'src/app/shared/common-structures/case-detail';
import { SystemicCaseDetailResponse } from './../../../services/Utils/systemicCaseDetailResponse';
import { DashboardUtils } from './dashboardUtils';
import { FilterView } from './filters.config';
import { EventEmitter } from '@angular/core';

@Injectable({
	providedIn: 'root',
})
export class DashboardService {
	caseList = new BehaviorSubject<CaseDetailV2[]>(null);
	dashboardViewBehaviorSubject = new BehaviorSubject<string>(null);
	selectedFilter = null;
	showUnassignBehaviorSubject = new BehaviorSubject<boolean>(false);
	filterChange =  new EventEmitter<any>();
	selectedSpotLightItem: any = {};
	constructor(
		private cmnServ: CommonService,
		private systemicServ: SystemicResourceService,
		private transferServ: TransferResourceService,
		private caseServ: PrepaCaseResourceService,
		private fepaCreditServ: FepaCreditResourceService,
		private searchDataServ: SearchDataService,
		private dualFileServ: PrepaDualFileResourceService,
		private userServ: UserResourceService,
		private _prepaSearchServ: PrepaSearchResourceService,
		private prepaMediationResourceService: PrepaMediationResourceService,
		private prepaPowerBiReportResourceService: PrepaPowerBiReportResourceService,
		private litAppealsDahsboardServ: LitigationAppealDashboardResourceService,
		private litAppealServ: LitigationAppealResourceService,
		private reportsResourceServ: ReportResourceService,
	) {}

	setDashboardView(_view: string) {
		return this.dashboardViewBehaviorSubject.next(_view);
	}

	getDashboardView(): BehaviorSubject<string> {
		return this.dashboardViewBehaviorSubject;
	}

	setShowUnassign(showUnassing: boolean) {
		this.showUnassignBehaviorSubject.next(showUnassing);
	}
	getShowUnassign(): Observable<boolean> {
		return this.showUnassignBehaviorSubject.asObservable();
	}

	setCases(_caseList: CaseDetailV2[]) {
		return this.caseList.next(_caseList);
	}

	getCases(): Observable<CaseDetailV2[]> {
		return this.caseList.asObservable();
	}

	setSelectedFilter = (_selectedFilter: FilterView) => (this.selectedFilter = _selectedFilter);
	getSelectedFilter = (): FilterView => this.selectedFilter;

	getOfficeStatistics(officeCode: string): Observable<any> {
		return this._prepaSearchServ.generateDasBoardRight(officeCode);
	}

	generateDasBoardLeftDocumentCategory(
		officeCode: string,
		posStateStatus: string,
		documentType: string,
		respdoctype = null
	): Observable<any> {
		return this._prepaSearchServ.generateDasBoardLeftDocumentCategory(
			posStateStatus,
			officeCode,
			documentType,
			respdoctype
		);
	}

	getFolderNameCaseFolderChargeIdByAcctOffice(officeCode: string) {
		return this._prepaSearchServ.getFolderNameCaseFolderChargeIdByAcctOffice1(officeCode);
	}

	getOfficeCaseListByPagination(
		officeCode: string,
		isPrivate: boolean,
		extraParams = {},
		pageIndex = 0,
		pageSize?: number
	): Observable<WsCasesResponseInterface> {
		let payload: CaseRequestParam = {
			isExactMatch: 'Y',
			ciIsFepa: isPrivate ? 'N' : 'Y',
		};
		if (officeCode) payload['ciAcctOfficeCode'] = officeCode;
		if (Object.keys(extraParams)?.length) payload = { ...payload, ...extraParams };
		return this.searchDataServ.searchCaseDetails(payload, pageIndex, pageSize);
	}

	/**
	 * @description get all cases by office code from web service
	 * @param userDetailId
	 */
	getAdrEligibleCaseListByOffice(
		officeCode: string,
		pageIndex = 0,
		pageSize?: number
	): Observable<WsCasesResponseInterface> {
		const statusCodePayload = [
			LookUpStatusCode.ELIGIBLE,
			LookUpStatusCode.AGREED_TO_MEDIATE,
			LookUpStatusCode.MEDIATION_SCHEDULED,
			LookUpStatusCode.REFERRED_TO_ENFORCEMENT,
			LookUpStatusCode.NO_LONGER_ELIGIBLE,
		].join('~');
		const payload: CaseRequestParam = {
			isExactMatch: 'Y',
			ciStatusCode: statusCodePayload,
			ciIsFepa: 'N',
		};
		if (officeCode) payload['ciAcctOfficeCode'] = officeCode;
		return this.searchDataServ.searchCaseDetails(payload);
	}

	getFepaCreditRequestsByEeocOffice(officeCode: string): Observable<any> {
		return this.fepaCreditServ.generateDashboardReport(officeCode ? officeCode : null, null, 60);
	}

	getFepaCreditRequestsByFepaOffice(officeCode: string): Observable<Array<CreditRequestResponse>> {
		return this.fepaCreditServ.generateDashboardReport(null, officeCode);
	}

	/**
	 * set case info in common service
	 * @param: caseInformation
	 */
	setCaseInformation(caseInformation: CaseDetailV2) {
		this.cmnServ.setCaseInformation(caseInformation);
	}

	setCaseFolders(caseFolders: CaseDetailV2) {
		this.cmnServ.setCaseFolders(caseFolders);
	}

	setCaseCabinets(caseCabinets: CaseDetailV2) {
		this.cmnServ.setCaseCabinets(caseCabinets);
	}

	/**
	 * @description Wrapper to an OpenAPI that unassigns all the inquiries for the input caseNumberArray
	 * @param: data object that holds caseNumberArray
	 * @param: requestBody contains assignedUserId as null
	 */
	unassignStaff(caseId: number) {
		return this.caseServ.assignStaff(caseId, []);
	}

	transferCase(transferRequest: TransferRequestVO): Observable<TransfersResponseVO> {
		return this.transferServ.requestTransfer(transferRequest);
	}

	updateCaseTransfer(caseTransferOutcomeVO: CaseTransferOutcomeVO): Observable<TransfersResponseVO> {
		return this.transferServ.updateCaseTransfer(caseTransferOutcomeVO);
	}

	getIncomingTransferCases(officeCode: string): Observable<Array<CaseDetailV2>> {
		return this._prepaSearchServ.searchCaseDetail2({
			isExactMatch: 'Y',
			ctTransferFromOfficeCd: officeCode,
		});
	}

	getOutgoingTransferCases(officeCode: string): Observable<Array<CaseDetailV2>> {
		return this._prepaSearchServ.searchCaseDetail2({
			isExactMatch: 'Y',
			ctTransferToOfficeCd: officeCode,
		});
	}

	getSystemicCaseInformation(isActive?: boolean): Observable<Array<SystemicCaseResponse>> {
		return this.systemicServ.getAllSystemicCases(isActive);
	}

	getSystemicCaseDetails(systemicId: number, isActive: boolean = true): Observable<SystemicCaseDetailResponse> {
		return this.systemicServ.getSystemicCaseDetails(systemicId, isActive);
	}

	getSubscribedSystemicCases(userId: string): Observable<number[]> {
		return this.systemicServ.getSubscribedSystemicCases(userId);
	}

	addSystemicCaseToCharges(
		id: number,
		systemicChargeVO: Array<SystemicChargeVO>
	): Observable<Array<SystemicCaseChargeResponse>> {
		return this.systemicServ.addSystemicCaseToCharges(id, systemicChargeVO);
	}

	updateSystemicCaseInformation(id: number, systemicCaseVO: SystemicCaseVO): Observable<SystemicCaseResponse> {
		return this.systemicServ.updateSystemicCaseInformation(id, systemicCaseVO);
	}

	subscribeSystemicCase(id: number): Observable<boolean> {
		return this.systemicServ.subscribeSystemicCase(id);
	}

	unSubscribeSystemicCase(id: number): Observable<boolean> {
		return this.systemicServ.unSubscribeSystemicCase(id);
	}
	deleteOrCloseSystemicCase(id: number, type: string) {
		if (type == 'remove') return this.deleteSystemicCase(id);
		return this.closeSystemicCase(id);
	}
	deleteSystemicCase(id: number): Observable<boolean> {
		return this.systemicServ.deleteSystemicCase(id);
	}

	closeSystemicCase(id: number): Observable<any> {
		return this.systemicServ.closeSystemicCase(id);
	}

	deleteSystemicCaseCharges(id: number, requestBody: Array<number>): Observable<boolean> {
		return this.systemicServ.deleteSystemicCaseCharges(id, requestBody);
	}

	getCasesByCaseNumber(caseNumbers: string[]): Observable<WsCasesResponseInterface> {
		return this.cmnServ.getCasesByCaseNumber(caseNumbers);
	}

	searchLogDetail(payload) {
		return this.searchDataServ.searchLogDetail(payload);
	}

	getIncomingNotifications(deferralOfficeCode: string): Observable<Array<DualFileResponse>> {
		return this.dualFileServ.getDeferralIncomingNotifications(deferralOfficeCode);
	}

	getDeferralAcknowledgments(deferralOfficeCode: string): Observable<Array<DualFileResponse>> {
		return this.dualFileServ.getDeferralAcknowledgments(deferralOfficeCode, null);
	}

	getOutgoingNotifications(receivingOfficeCode: string): Observable<Array<DualFileResponse>> {
		return this.dualFileServ.getOutgoingNotifications(receivingOfficeCode);
	}

	getReceivingAcknowledgments(receivingOfficeCode: string): Observable<Array<DualFileResponse>> {
		return this.dualFileServ.getReceivingAcknowledgments(receivingOfficeCode);
	}

	/**
	 * @description Call service to assign one or more staffs to a case.
	 * @param caseNumber
	 * @param staffAssignmentVO
	 * @note The old service is removed as per requirement change
	 */
	assignMultipleStaffs(caseId: number, staffAssignmentVO?: Array<StaffAssignmentVO>): Observable<boolean> {
		staffAssignmentVO.forEach((item) => {
			item.isAdr = item.isAdr ?? false;
		});
		return this.caseServ.assignStaff(caseId, staffAssignmentVO);
	}

	assignMediationStaff(caseId: number, staffAssignmentVO?: Array<StaffAssignmentVO>) {
		return this.prepaMediationResourceService.assignMediationStaff(caseId, staffAssignmentVO);
	}

	/**
	 * Add assignees to a Litigation Appeal
	 * Add assignees to a Litigation Appeal (Enforcement, Amicus, Defensive)
	 * @param appealId
	 * @param requestBody
	 */
	public createAssignmentsForAppeal(appealId: number, requestBody: Array<string>): Observable<any> {
		return this.litAppealServ.createAssignmentsForAppeal(appealId, requestBody);
	}

	/**
	 * Sets assignees to a Litigation Appeal
	 * Sets assignees to a Litigation Appeal. Removes assignments as necessary (Enforcement, Amicus, Defensive)
	 * @param appealId
	 * @param requestBody
	 */
	public setAssignmentsForAppeal(appealId: number, requestBody: Array<string>): Observable<any> {
		return this.litAppealServ.setAssignmentsForAppeal(appealId, requestBody);
	}

	/**
	 * Get assignments for a Litigation Appeal
	 * Get assignments for a Litigation Appeal (Enforcement, Amicus, Defensive)
	 * @param appealId
	 */
	public getAssignmentsForAppeal(appealId: number): Observable<Array<AppealAssignmentVO>> {
		return this.litAppealServ.getAssignmentsForAppeal(appealId);
	}

	/**
	 * @description Call the service to retrieve the PowerBI Reports
	 * @returns list of PowerBI reports
	 */
	getReports() {
		return this.prepaPowerBiReportResourceService.getPowerBiReport();
	}

	/**
	 * @description To get all the appeals data
	 */
	public getAppealDashboardData(assignedTo?: string): Observable<Array<AppealDashboard>> {
		return this.litAppealsDahsboardServ.getAppealDashboardData(assignedTo);
	}

	/**
	 * @param appealType
	 */
	public getAppealDashboardDataByType(appealType): Observable<Array<AppealDashboard>> {
		return this.litAppealsDahsboardServ.getAppealDashboardDataByType(appealType);
	}

	public generatePendingInventoryReport(reportName, reqParam): Observable<Array<PendingReportResponseSummary>> {
		return this.reportsResourceServ.generatePendingInventoryReport(reportName.queryCode, reqParam);
	}

	public generateEventReport(reportName, reqParam): Observable<Array<any>> {
		return this.reportsResourceServ.generateEventReport(reportName.queryCode, reqParam);
	}
}
